import React, { useEffect, useState, useRef } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, SectionPad, Badge } from "../../components/ui-extras";
import BulkUploader from "../../components/BulkUploader";
import BulkPriceReportModal from "../../components/BulkPriceReportModal";
import InternalBulkTestModal from "../../components/InternalBulkTestModal";
import { useAuth } from "../../lib/auth";
import { UploadSimple, CheckCircle, Trash, ArrowsClockwise, EnvelopeSimple } from "@phosphor-icons/react";
import { displayProduct } from "../../lib/products";
import { Can } from "../../lib/rbac";

export default function PriceLists() {
  const [entries, setEntries] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [network, setNetwork] = useState("");
  // Phase 3E-9 (2026-02-27): filter-side country co-dispatch so the
  // Petro-Pass filter row can distinguish CA (CAD/L) from US (USD/GAL).
  const [filterCountry, setFilterCountry] = useState("");
  const [q, setQ] = useState("");
  const [productFilter, setProductFilter] = useState([]);  // canonical codes; empty = all
  const [provinceFilter, setProvinceFilter] = useState([]);  // 2-letter codes; empty = all
  // Date filter — two modes:
  //   "effective_on" — show prices that were LIVE on this date (most useful
  //                    for "what was billed for tx on day X").
  //   "effective_date" — show only the upload that was dated this exact day
  //                      (for inspecting a single supplier feed).
  // Empty `dateFilter` means "show latest entries regardless of date" (legacy
  // behaviour). Both modes are exposed as a toggle next to the date input.
  const [dateMode, setDateMode] = useState("effective_on");
  const [dateFilter, setDateFilter] = useState("");
  const [showUp, setShowUp] = useState(false);
  const [showBulkUp, setShowBulkUp] = useState(false);
  const [showBulkSend, setShowBulkSend] = useState(false);
  const [showInternalTest, setShowInternalTest] = useState(false);
  const { user } = useAuth();
  const [upNetwork, setUpNetwork] = useState("esso");
  const [upCountry, setUpCountry] = useState("CA");
  const [upEffDate, setUpEffDate] = useState(new Date().toISOString().slice(0, 10));
  // Effective-date preview safety (R1) — populated by dry-run parse of
  // the selected file. `srcDate.confidence` is one of
  // single | multiple | absent | unparseable | non_authoritative.
  const [srcDate, setSrcDate] = useState({
    confidence: null, source: null, candidates: [], reason: "",
  });
  const [ackMismatch, setAckMismatch] = useState(false);
  const [ackDateless, setAckDateless] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [dupPrompt, setDupPrompt] = useState(null); // { msg } when 409 fires
  const [aliases, setAliases] = useState([]);
  const [aliasForm, setAliasForm] = useState({ network: "esso", alias_station_id: "", canonical_station_id: "", note: "" });
  const fileRef = useRef();

  const load = async () => {
    const params = {
      network: network || undefined,
      country: filterCountry || undefined,
      q: q || undefined,
      product_canonical: productFilter.length ? productFilter.join(",") : undefined,
      province: provinceFilter.length ? provinceFilter.join(",") : undefined,
      limit: 400,
    };
    if (dateFilter) {
      params[dateMode] = dateFilter;
    }
    const [e, u, a, al] = await Promise.all([
      api.get("/price-lists/entries", { params }),
      api.get("/price-lists/uploads"),
      api.get("/price-alerts"),
      api.get("/station-aliases"),
    ]);
    setEntries(e.data); setUploads(u.data); setAlerts(a.data); setAliases(al.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [network, filterCountry, q, productFilter, provinceFilter, dateFilter, dateMode]);

  const doUpload = async (force = false) => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Choose a file");
    const fd = new FormData();
    fd.append("network", upNetwork);
    if (upNetwork === "petro_pass") fd.append("country", upCountry);
    fd.append("file", f);
    if (upEffDate) fd.append("effective_date", upEffDate);
    if (force) fd.append("force", "true");
    if (srcDate.dateless && ackDateless) fd.append("dateless_acknowledged", "true");
    if (
      srcDate.confidence === "single"
      && srcDate.source
      && upEffDate
      && upEffDate !== srcDate.source
      && ackMismatch
    ) {
      fd.append("effective_date_override_acknowledged", "true");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/price-lists/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const skipped = (data.skipped_older_count || 0) + (data.skipped_future_count || 0);
      toast.success(`${data.rows} rows saved · effective ${data.effective_date} · ${data.alerts} alerts`
        + (skipped ? ` · ${skipped} rows skipped (${data.skipped_older_count || 0} older, ${data.skipped_future_count || 0} later)` : ""),
        { duration: skipped ? 12000 : 4000 });
      setShowUp(false); setDupPrompt(null);
      setSrcDate({ confidence: null, source: null, candidates: [], reason: "" });
      setAckMismatch(false);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409) {
        setDupPrompt(typeof detail === "object" && detail
          ? { msg: detail.message, d: detail }
          : { msg: (typeof detail === "string" ? detail : "Possible duplicate price list.") });
      } else if (
        e.response?.status === 400
        && detail && typeof detail === "object"
        && detail.code === "EFFECTIVE_DATE_MISMATCH"
      ) {
        setSrcDate({
          confidence: "single",
          source: detail.source_effective_date,
          candidates: [detail.source_effective_date],
          reason: "Effective date detected from price report contents.",
        });
        toast.error(
          `Effective date mismatch — detected ${detail.source_effective_date}, `
          + `selected ${detail.selected_effective_date}. Acknowledge to continue.`
        );
      } else {
        toast.error((typeof detail === "string" ? detail : detail?.message) || "Upload failed");
      }
    } finally { setBusy(false); }
  };

  const previewSourceDate = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    setAckMismatch(false);
    setAckDateless(false);
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append("network", upNetwork);
      if (upNetwork === "petro_pass") fd.append("country", upCountry);
      fd.append("file", f);
      fd.append("dry_run", "true");
      if (upEffDate) fd.append("effective_date", upEffDate);
      const { data } = await api.post(
        "/price-lists/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const meta = {
        confidence: data.source_effective_date_confidence || null,
        source: data.source_effective_date || null,
        candidates: data.source_effective_date_candidates || [],
        reason: data.source_effective_date_reason || "",
        check: data.date_check || null,
        dateless: !!data.dateless_requires_ack,
      };
      setSrcDate(meta);
      if (meta.confidence === "single" && meta.source) {
        setUpEffDate(meta.source);
      }
    } catch (e) {
      // Preview failure should not block manual upload; keep any prior
      // metadata cleared so the user is not misled.
      setSrcDate({ confidence: null, source: null, candidates: [], reason: "" });
    } finally { setPreviewing(false); }
  };


  const deleteUpload = async (u) => {
    setDeleting(true);
    try {
      const { data } = await api.delete(`/price-lists/uploads/${u.upload_id}?cascade=true`);
      toast.success(`Deleted "${u.filename}" · ${data.entries_deleted} entries · ${data.alerts_deleted} alerts`);
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    finally { setDeleting(false); }
  };

  const resolve = async (id) => { await api.post(`/price-alerts/${id}/resolve`); toast.success("Resolved"); load(); };

  const addAlias = async () => {
    if (!aliasForm.alias_station_id || !aliasForm.canonical_station_id) {
      return toast.error("Both IDs are required");
    }
    try {
      await api.post("/station-aliases", aliasForm);
      toast.success("Alias saved");
      setAliasForm({ ...aliasForm, alias_station_id: "", canonical_station_id: "", note: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const delAlias = async (id) => {
    if (!window.confirm("Remove this alias?")) return;
    try { await api.delete(`/station-aliases/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const reparseUpload = async (u) => {
    if (!u.can_reparse) {
      toast.error("This upload predates raw-file storage. Delete and re-upload to use new parser.");
      return;
    }
    if (!window.confirm(`Re-parse "${u.filename}" with the latest parser? This replaces all ${u.row_count} entries from this upload.`)) return;
    try {
      const { data } = await api.post(`/price-lists/uploads/${u.upload_id}/reparse`);
      toast.success(`Re-parsed · replaced ${data.rows_replaced} → ${data.rows_inserted} rows · effective ${data.effective_date}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-parse failed");
    }
  };

  return (
    <SectionPad>
      {showBulkSend && <BulkPriceReportModal onClose={() => setShowBulkSend(false)} />}
      {showInternalTest && <InternalBulkTestModal onClose={() => setShowInternalTest(false)} />}
      <PageHeader
        title="Price lists"
        subtitle="PROVIDERS / RACK PRICES"
        testId="pricelists-header"
        actions={
          <>
            <Can perm="pricing.upload">
            <button
              className="gf-btn gf-btn-outline"
              onClick={() => setShowBulkUp(true)}
              data-testid="bulk-upload-pricelist-button"
              title="Upload many price lists at once"
            >
              <UploadSimple size={14}/> Bulk upload
            </button>
            {user?.role === "admin" && (
              <button
                className="gf-btn gf-btn-outline"
                onClick={() => setShowBulkSend(true)}
                data-testid="bulk-send-pricelist-button"
                title="Email each opted-in active customer their own price list for tomorrow"
              >
                <EnvelopeSimple size={14}/> Send Price List to All Customers
              </button>
            )}
            {user?.role === "admin" && (
              <button
                className="gf-btn gf-btn-outline"
                onClick={() => setShowInternalTest(true)}
                data-testid="internal-bulk-test-button"
                title="Send the bulk Price Report only to the approved internal test addresses"
              >
                <EnvelopeSimple size={14}/> Internal Bulk Email Test
              </button>
            )}
            <button
              className="gf-btn gf-btn-primary"
              onClick={() => setShowUp(true)}
              data-testid="upload-pricelist-button"
            >
              <UploadSimple size={14}/> Upload price list
            </button>
            </Can>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 gf-card">
          <div className="gf-card-header">
            <div className="flex items-center gap-2">
              <select
                className="gf-input"
                value={
                  network === "petro_pass" && filterCountry === "CA" ? "petro_pass_ca"
                  : network === "petro_pass" && filterCountry === "US" ? "petro_pass_us"
                  : network
                }
                onChange={(e) => {
                  const v = e.target.value;
                  // Phase 3E-9: atomic-pair options for Petro-Pass so a
                  // filter switch always applies both network+country.
                  if (v === "petro_pass_ca") { setNetwork("petro_pass"); setFilterCountry("CA"); return; }
                  if (v === "petro_pass_us") { setNetwork("petro_pass"); setFilterCountry("US"); return; }
                  setNetwork(v);
                  setFilterCountry("");
                }}
                style={{ width: 220 }}
                data-testid="price-network-filter"
              >
                <option value="">All networks</option>
                <option value="esso">Esso ¢/L (CAD)</option>
                <option value="pipeline">Pipeline ¢/L (CAD)</option>
                <option value="petro_pass_ca">Petro-Pass Canada ¢/L (CAD)</option>
                <option value="petro_pass_us">Petro-Pass USA USD/GAL</option>
              </select>
              <input className="gf-input" placeholder="Search station…" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 200 }} data-testid="price-search" />
            </div>
            <div className="overline">{entries.length} ENTRIES</div>
          </div>
          {/* Multi-select product filter — click to toggle. Empty = all products. */}
          <div className="px-4 py-2 border-b border-zinc-100 flex items-center gap-2 flex-wrap">
            <span className="overline mr-1">PRODUCTS</span>
            {["DIESEL_LS", "DSL_EFF_LS", "RFR", "DEFD", "DIESEL_DYED", "DSL_EFF_DYED", "REGULAR", "PREMIUM"].map((code) => {
              const checked = productFilter.includes(code);
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => setProductFilter((cur) =>
                    cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]
                  )}
                  data-testid={`price-product-${code}`}
                  className={`px-2 py-0.5 text-xs font-mono tracking-widest border ${
                    checked ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
                  }`}
                >
                  <span className="mr-1">{checked ? "✓" : ""}</span>{code}
                </button>
              );
            })}
            {productFilter.length > 0 && (
              <button
                type="button"
                className="text-xs text-zinc-500 hover:text-zinc-900 normal-case ml-2"
                onClick={() => setProductFilter([])}
                data-testid="price-product-clear"
              >Clear</button>
            )}
            <span className="text-xs text-zinc-400 ml-auto">
              {productFilter.length === 0 ? "All products" : `${productFilter.length} selected`}
            </span>
          </div>
          {/* Province filter — same pattern as products. Two-letter CA codes. */}
          <div className="px-4 py-2 border-b border-zinc-100 flex items-center gap-2 flex-wrap">
            <span className="overline mr-1">PROVINCE</span>
            {["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"].map((code) => {
              const checked = provinceFilter.includes(code);
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => setProvinceFilter((cur) =>
                    cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]
                  )}
                  data-testid={`price-province-${code}`}
                  className={`px-2 py-0.5 text-xs font-mono tracking-widest border ${
                    checked ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
                  }`}
                >
                  <span className="mr-1">{checked ? "✓" : ""}</span>{code}
                </button>
              );
            })}
            {provinceFilter.length > 0 && (
              <button
                type="button"
                className="text-xs text-zinc-500 hover:text-zinc-900 normal-case ml-2"
                onClick={() => setProvinceFilter([])}
                data-testid="price-province-clear"
              >Clear</button>
            )}
            <span className="text-xs text-zinc-400 ml-auto">
              {provinceFilter.length === 0 ? "All provinces" : `${provinceFilter.length} selected`}
            </span>
          </div>
          {/* Date filter — pick a specific day to see what was live (or what was uploaded) */}
          <div className="px-4 py-2 border-b border-zinc-100 flex items-center gap-2 flex-wrap">
            <span className="overline mr-1">DATE</span>
            <select
              className="gf-input"
              style={{ width: "auto", minWidth: 180 }}
              value={dateMode}
              onChange={(e) => setDateMode(e.target.value)}
              data-testid="price-date-mode"
            >
              <option value="effective_on">Effective on (live price)</option>
              <option value="effective_date">Exact upload date</option>
            </select>
            <input
              type="date"
              className="gf-input"
              style={{ width: 160 }}
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              data-testid="price-date-input"
              placeholder="YYYY-MM-DD"
            />
            {dateFilter && (
              <button
                type="button"
                className="text-xs text-zinc-500 hover:text-zinc-900 normal-case"
                onClick={() => setDateFilter("")}
                data-testid="price-date-clear"
              >Clear</button>
            )}
            <span className="text-xs text-zinc-400 ml-auto">
              {dateFilter
                ? (dateMode === "effective_on"
                    ? `Showing prices live on ${dateFilter}`
                    : `Showing rows dated exactly ${dateFilter}`)
                : "Showing latest prices"}
            </span>
          </div>
          <div className="overflow-x-auto" style={{ maxHeight: 560 }}>
            <table className="gf-table">
              <thead>
                <tr><th>STATION</th><th>PROV / STATE</th><th>PRODUCT</th><th className="text-right">PRICE</th><th className="text-right">Δ</th><th>EFF DATE</th></tr>
              </thead>
              <tbody>
                {entries.map((r, i) => {
                  const isUsPetroPass = r.network === "petro_pass" && r.country === "US";
                  const priceValue = isUsPetroPass
                    ? r.index_price_per_gal
                    : r.net_price;
                  const priceLabel = isUsPetroPass ? "USD/GAL" : "¢/L (CAD)";
                  return (
                  <tr key={`${r.station_id}-${r.product}-${r.effective_date}-${i}`} data-testid={`plrow-${r.network}-${r.country || 'CA'}-${r.station_id}`}>
                    <td><div className="text-sm">{r.station_name}</div><div className="text-xs text-zinc-500 font-mono">{r.network === "petro_pass" ? "PETRO-PASS" : r.network.toUpperCase()}{r.country ? ` · ${r.country}` : ""} · #{r.station_id}</div></td>
                    <td className="font-mono">{r.province || r.state || ""}</td>
                    <td className="font-mono text-xs">{displayProduct(r.product, r.network)}</td>
                    <td className="gf-num font-semibold" data-testid={`plrow-price-${r.station_id}`}>
                      {priceValue != null ? priceValue.toFixed(isUsPetroPass ? 4 : 2) : "—"}
                      <div className="text-[10px] text-zinc-400 font-mono">{priceLabel}</div>
                    </td>
                    <td className="gf-num" data-testid={`plrow-delta-${r.station_id}`}>
                      {(() => {
                        // P4-N5D (2026-02): PP-USA carries a native USD/GAL delta
                        // (`price_change_usd_gal`) derived read-time by the backend.
                        // Distinguish missing/null from numeric zero explicitly —
                        // `null` == no comparable previous observation (blank);
                        // `0` == genuine no-change (also blank, matching CAD/L
                        // convention which only shows a badge when != 0).
                        if (isUsPetroPass) {
                          const d = r.price_change_usd_gal;
                          if (d == null || d === 0) return null;
                          return (
                            <Badge variant={d > 0 ? "error" : "success"} data-testid={`plrow-delta-badge-${r.station_id}`}>
                              {d > 0 ? "+" : ""}{d.toFixed(4)}
                              <span className="text-[10px] text-zinc-400 font-mono ml-1">USD/GAL</span>
                            </Badge>
                          );
                        }
                        return r.price_change_cpl !== 0 && r.price_change_cpl != null && (
                          <Badge variant={r.price_change_cpl > 0 ? "error" : "success"} data-testid={`plrow-delta-badge-${r.station_id}`}>
                            {r.price_change_cpl > 0 ? "+" : ""}{r.price_change_cpl?.toFixed(2)}
                            {!isUsPetroPass && <span className="text-[10px] text-zinc-400 font-mono ml-1">¢/L</span>}
                          </Badge>
                        );
                      })()}
                    </td>
                    <td className="font-mono text-xs">{r.effective_date}{r.effective_time && r.effective_time !== "00:00:00" ? ` ${r.effective_time}` : ""}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-4">
          <div className="gf-card">
            <div className="gf-card-header"><h3>Recent uploads</h3></div>
            <div className="overflow-x-auto">
              <table className="gf-table">
                <thead><tr><th>NETWORK</th><th>FILE</th><th>EFF DATE</th><th className="text-right">ROWS</th><th></th></tr></thead>
                <tbody>
                  {uploads.map((u, idx) => (
                    <tr key={`${u.upload_id}-${idx}`}>
                      <td className="font-mono text-xs">{u.network.toUpperCase()}</td>
                      <td className="text-xs truncate" style={{ maxWidth: 160 }}>{u.filename}</td>
                      <td className="font-mono text-xs">{u.effective_date}</td>
                      <td className="gf-num">{u.row_count}</td>
                      <td className="text-right">
                        <button
                          className="gf-btn gf-btn-ghost"
                          onClick={() => reparseUpload(u)}
                          disabled={!u.can_reparse}
                          data-testid={`reparse-upload-${u.upload_id}`}
                          title={u.can_reparse
                            ? "Re-parse with latest parser (replaces all entries from this upload)"
                            : "Re-parse unavailable: this upload predates raw-file storage. Delete and re-upload to enable."}
                          style={!u.can_reparse ? { opacity: 0.35, cursor: "not-allowed" } : undefined}
                        >
                          <ArrowsClockwise size={14}/>
                        </button>
                        <button className="gf-btn gf-btn-ghost" onClick={() => setConfirmDelete(u)}
                          data-testid={`delete-upload-${u.upload_id}`}
                          title="Delete price list">
                          <Trash size={14}/>
                        </button>
                      </td>
                    </tr>
                  ))}
                  {uploads.length === 0 && <tr><td colSpan={5} className="text-center text-zinc-400 py-6">No uploads yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="gf-card">
            <div className="gf-card-header"><h3>Alerts</h3><Badge variant="warning">{alerts.filter((a) => a.status === "open").length} OPEN</Badge></div>
            <div className="overflow-x-auto" style={{ maxHeight: 360 }}>
              <table className="gf-table">
                <thead><tr><th>STATION</th><th className="text-right">Δ</th><th></th></tr></thead>
                <tbody>
                  {alerts.map((a) => (
                    <tr key={a.alert_id}>
                      <td><div className="text-sm">{a.station_name}</div><div className="text-xs text-zinc-500 font-mono">{a.network.toUpperCase()}</div></td>
                      <td className="gf-num"><Badge variant={a.change > 0 ? "error" : "success"}>{a.change > 0 ? "+" : ""}{a.change?.toFixed(2)}</Badge></td>
                      <td>{a.status === "open" ? <button className="gf-btn gf-btn-ghost" onClick={() => resolve(a.alert_id)} data-testid={`resolve-alert-${a.alert_id}`}><CheckCircle size={14}/></button> : <Badge variant="success">RESOLVED</Badge>}</td>
                    </tr>
                  ))}
                  {alerts.length === 0 && <tr><td colSpan={3} className="text-center text-zinc-400 py-6">No alerts.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className="gf-card mb-6" data-testid="station-aliases-card">
        <div className="gf-card-header">
          <h3>Station ID aliases <span className="text-xs text-zinc-400">· {aliases.length}</span></h3>
          <span className="text-xs text-zinc-500 font-mono">
            Bridges a transaction-file station ID → the canonical price-list station ID
          </span>
        </div>
        <div className="gf-card-body">
          <div className="text-xs text-zinc-500 mb-3">
            Use this when Esso transaction CSVs use a different station ID than the
            Esso price list for the same physical station. Aliases are checked
            during transaction pricing if a direct station_id lookup fails.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-3">
            <div className="md:col-span-2">
              <div className="overline mb-1">Network</div>
              <select className="gf-input" value={aliasForm.network}
                onChange={(e) => setAliasForm({ ...aliasForm, network: e.target.value })}
                data-testid="alias-network">
                <option value="esso">Esso ¢/L (CAD)</option><option value="pipeline">Pipeline ¢/L (CAD)</option><option value="petro_pass">Petro-Pass</option>
              </select>
            </div>
            <div className="md:col-span-3">
              <div className="overline mb-1">Tx file ID (alias)</div>
              <input className="gf-input font-mono" value={aliasForm.alias_station_id}
                onChange={(e) => setAliasForm({ ...aliasForm, alias_station_id: e.target.value })}
                placeholder="e.g. 29466" data-testid="alias-from" />
            </div>
            <div className="md:col-span-3">
              <div className="overline mb-1">Price-list ID (canonical)</div>
              <input className="gf-input font-mono" value={aliasForm.canonical_station_id}
                onChange={(e) => setAliasForm({ ...aliasForm, canonical_station_id: e.target.value })}
                placeholder="e.g. 524620" data-testid="alias-to" />
            </div>
            <div className="md:col-span-3">
              <div className="overline mb-1">Note (optional)</div>
              <input className="gf-input" value={aliasForm.note}
                onChange={(e) => setAliasForm({ ...aliasForm, note: e.target.value })}
                placeholder="ESSO SAULT STE MARIE TRUNK" />
            </div>
            <div className="md:col-span-1 flex items-end">
              <button className="gf-btn gf-btn-primary w-full" onClick={addAlias} data-testid="alias-save">
                Add
              </button>
            </div>
          </div>
          <div className="overflow-auto" style={{ maxHeight: 280 }}>
            <table className="gf-table">
              <thead><tr>
                <th>NETWORK</th><th>TX ID</th><th>→</th><th>PRICE-LIST ID</th><th>STATION NAME</th><th>NOTE</th><th></th>
              </tr></thead>
              <tbody>
                {aliases.map((a) => (
                  <tr key={a.alias_id}>
                    <td className="font-mono">{a.network.toUpperCase()}</td>
                    <td className="font-mono">{a.alias_station_id}</td>
                    <td className="text-zinc-400">→</td>
                    <td className="font-mono">{a.canonical_station_id}</td>
                    <td className="text-xs">{a.canonical_station_name || "—"}</td>
                    <td className="text-xs text-zinc-500">{a.note || "—"}</td>
                    <td className="text-right">
                      <button className="gf-btn gf-btn-ghost" onClick={() => delAlias(a.alias_id)}
                        data-testid={`alias-delete-${a.alias_id}`}>
                        <Trash size={14}/>
                      </button>
                    </td>
                  </tr>
                ))}
                {aliases.length === 0 && <tr><td colSpan={7} className="text-center text-zinc-400 py-6">No aliases yet.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-zinc-500 mt-3 font-mono">
            After adding aliases, re-price flagged transactions from Transactions → Re-price.
          </div>
        </div>
      </div>

      {showUp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !busy && setShowUp(false)}>
          <div className="bg-white w-full max-w-md border border-zinc-200" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header"><h3>Upload price list</h3></div>
            <div className="gf-card-body space-y-3">
              <div><div className="overline mb-1">Lane</div>
                <select
                  className="gf-input"
                  value={
                    upNetwork === "petro_pass" && upCountry === "CA" ? "petro_pass_ca"
                    : upNetwork === "petro_pass" && upCountry === "US" ? "petro_pass_us"
                    : upNetwork
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    // Phase 3E-9: atomic-pair values so an operator can
                    // never dispatch Petro-Pass with a stale country.
                    if (v === "petro_pass_ca") { setUpNetwork("petro_pass"); setUpCountry("CA"); return; }
                    if (v === "petro_pass_us") { setUpNetwork("petro_pass"); setUpCountry("US"); return; }
                    setUpNetwork(v);
                  }}
                  data-testid="upload-network"
                >
                <option value="esso">Esso ¢/L (CSV)</option>
                <option value="pipeline">Pipeline CAD/L (XLS)</option>
                <option value="petro_pass_ca">Petro-Pass Canada CAD/L (XLSX/CSV)</option>
                <option value="petro_pass_us">Petro-Pass USA USD/GAL (CSV)</option>
                </select>
              </div>
              {upNetwork === "petro_pass" && (
                <div data-testid="upload-country-wrap" className="hidden">
                  {/* Retained for backwards-compat / testability. Country
                      is set atomically by the Lane dropdown above. */}
                  <select value={upCountry} onChange={(e) => setUpCountry(e.target.value)} data-testid="upload-country">
                    <option value="CA">Canada — CAD $/L</option>
                    <option value="US">USA — USD $/GAL</option>
                  </select>
                </div>
              )}
              <div><div className="overline mb-1">Expected effective date</div>
                <input type="date" className="gf-input" value={upEffDate}
                  onChange={(e) => { setUpEffDate(e.target.value); setAckMismatch(false); }}
                  data-testid="upload-effective-date" />
                <div className="text-xs text-zinc-500 mt-1">
                  Checked against the effective date in the price file. Supplier dates are never overwritten; only rows with this date are saved, and older or later rows are skipped and listed.
                </div>
                {srcDate.confidence === "single" && srcDate.source && upEffDate === srcDate.source && (
                  <div className="text-xs text-emerald-700 mt-2" data-testid="src-date-detected">
                    Effective date detected from price report: <b>{srcDate.source}</b>
                  </div>
                )}
                {srcDate.confidence === "single" && srcDate.source && upEffDate && upEffDate !== srcDate.source && (
                  <div className="text-xs mt-2 p-2 border border-amber-300 bg-amber-50" data-testid="src-date-mismatch">
                    <div className="font-semibold text-amber-800 mb-1">
                      Selected effective date does not match the effective date detected in this price report.
                    </div>
                    <div>Report effective date: <b>{srcDate.source}</b></div>
                    <div>Selected effective date: <b>{upEffDate}</b></div>
                    <div className="mt-1 text-amber-900" data-testid="src-date-mismatch-blocked">Upload is blocked. Select the report's effective date to continue.</div>
                  </div>
                )}
                {(srcDate.confidence === "multiple"
                  || srcDate.confidence === "absent"
                  || srcDate.confidence === "unparseable"
                  || srcDate.confidence === "non_authoritative") && (
                  <div className="text-xs text-zinc-600 mt-2 p-2 border border-zinc-200 bg-zinc-50" data-testid="src-date-manual">
                    {srcDate.confidence === "non_authoritative"
                      ? "Report contains a date, but it is not confirmed as the price effective date. Please select the effective date manually."
                      : "Effective date could not be determined unambiguously from this report. Please select it manually."}
                    {srcDate.candidates.length > 0 && (
                      <div className="mt-1">Candidates: {srcDate.candidates.slice(0, 5).join(", ")}</div>
                    )}
                  </div>
                )}
              </div>
              <div><div className="overline mb-1">File</div>
                <input ref={fileRef} type="file" className="gf-input" accept=".csv,.xls,.xlsx"
                  onChange={() => previewSourceDate()}
                  data-testid="upload-file-input" />
                {previewing && (
                  <div className="text-xs text-zinc-500 mt-1" data-testid="src-date-previewing">
                    Detecting effective date…
                  </div>
                )}
                {srcDate.check && !srcDate.dateless && <DateCheck c={srcDate.check} />}
                {srcDate.dateless && (
                  <DatelessAck date={upEffDate} checked={ackDateless} onChange={setAckDateless} />
                )}
              </div>
              <div className="text-xs text-zinc-500">Price changes ≥ 3 CPL will create an alert automatically.</div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => setShowUp(false)} disabled={busy}>Cancel</button>
              <button
                className="gf-btn gf-btn-primary"
                onClick={() => doUpload(false)}
                disabled={
                  busy
                  || (srcDate.confidence === "single"
                      && srcDate.source
                      && upEffDate
                      && upEffDate !== srcDate.source)
                  || (srcDate.dateless && (!upEffDate || !ackDateless))
                }
                data-testid="do-upload-button"
              >{busy ? "Uploading…" : "Upload"}</button>
            </div>
          </div>
        </div>
      )}
      {dupPrompt && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
             onClick={() => !busy && setDupPrompt(null)}>
          <div className="bg-white w-full max-w-md border border-zinc-200" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header"><h3>Possible duplicate</h3></div>
            <div className="gf-card-body">
              <div className="text-sm whitespace-pre-wrap" data-testid="dup-message">{dupPrompt.msg}</div>
              {dupPrompt.d?.code === "DUPLICATE_DATE" && (
                <div className="text-xs mt-2 grid grid-cols-3 gap-2 font-mono" data-testid="dup-conflict-counts">
                  <div>Overlapping <b>{dupPrompt.d.overlapping}</b></div>
                  <div className="text-emerald-700">Identical <b>{dupPrompt.d.identical}</b></div>
                  <div className="text-rose-700">Different price <b>{dupPrompt.d.changed}</b></div>
                </div>
              )}
              <div className="text-xs text-amber-900 mt-3 p-2 border border-amber-300 bg-amber-50" data-testid="dup-override-implications">
                Uploading anyway ADDS a second set of entries for the same effective date. Existing entries are
                not replaced and duplicates are not merged automatically, so the same station/product can then
                have two saved prices for that date. Your override is recorded in the upload audit.
                If the old entries are stale (e.g. seed data), delete them first from the Recent uploads list.
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => setDupPrompt(null)} disabled={busy}
                data-testid="dup-cancel">Cancel</button>
              <button className="gf-btn gf-btn-primary" onClick={() => doUpload(true)} disabled={busy}
                data-testid="dup-force-upload">
                {busy ? "Uploading…" : "Upload anyway"}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
             onClick={() => !deleting && setConfirmDelete(null)}>
          <div className="bg-white w-full max-w-md border border-zinc-200" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header"><h3>Delete price list?</h3></div>
            <div className="gf-card-body">
              <div className="text-sm space-y-2">
                <div><span className="overline">FILE</span><div className="font-mono mt-1">{confirmDelete.filename}</div></div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div><span className="overline">NETWORK</span><div className="font-mono mt-1">{confirmDelete.network.toUpperCase()}</div></div>
                  <div><span className="overline">EFF DATE</span><div className="font-mono mt-1">{confirmDelete.effective_date}</div></div>
                  <div><span className="overline">ROWS</span><div className="font-mono mt-1">{confirmDelete.row_count}</div></div>
                </div>
                <div className="text-xs text-zinc-500 mt-3">
                  This permanently removes the upload, all price entries it created, and any related price alerts. Customer price reports will fall back to the previous active price list.
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => setConfirmDelete(null)} disabled={deleting}>Cancel</button>
              <button className="gf-btn gf-btn-danger" onClick={() => deleteUpload(confirmDelete)} disabled={deleting}
                data-testid="confirm-delete-upload">
                {deleting ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showBulkUp && (
        <BulkUploader
          open={showBulkUp}
          onClose={() => setShowBulkUp(false)}
          onAllDone={() => load()}
          title="Bulk upload price lists"
          accept=".csv,.xls,.xlsx,.tsv,.txt"
          defaultMeta={{ network: "esso", effective_date: "" }}
          metaFields={[
            {
              key: "network",
              label: "NETWORK",
              type: "select",
              options: [
                { value: "esso", label: "Esso" },
                { value: "pipeline", label: "Pipeline" },
                { value: "petro_pass", label: "Petro-Pass" },
              ],
            },
            { key: "effective_date", label: "EFFECTIVE DATE", type: "date" },
          ]}
          detectMeta={(file) => {
            // Filename hints help the user not have to fix every row by hand.
            const n = file.name.toLowerCase();
            const meta = {};
            if (n.includes("pipeline") || n.endsWith(".xls") || n.endsWith(".xlsx")) meta.network = "pipeline";
            else if (n.includes("esso") || n.endsWith(".tsv") || n.endsWith(".csv") || n.endsWith(".txt")) meta.network = "esso";
            // ISO date in filename (YYYY-MM-DD) → effective_date prefill.
            const m = n.match(/(\d{4}-\d{2}-\d{2})/);
            if (m) meta.effective_date = m[1];
            return meta;
          }}
          uploadOne={async (file, meta) => {
            const fd = new FormData();
            fd.append("network", meta.network || "esso");
            fd.append("file", file);
            if (meta.effective_date) fd.append("effective_date", meta.effective_date);
            // Bulk never overrides duplicate guards; conflicting or dateless
            // Esso files are rejected (nothing saved) and must use single upload.
            fd.append("bulk", "true");
            let data;
            try {
              ({ data } = await api.post("/price-lists/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
              }));
            } catch (e) {
              const d = e.response?.data?.detail;
              if (d && typeof d === "object") e.response.data.detail = d.message || JSON.stringify(d);
              throw e;
            }
            const skipped = (data.skipped_older_count || 0) + (data.skipped_future_count || 0);
            return { summary: `${data.rows} rows · ${data.alerts} alerts · eff ${data.effective_date}${skipped ? ` · ${skipped} skipped (other dates)` : ""}` };
          }}
        />
      )}
    </SectionPad>
  );
}


function DatelessAck({ date, checked, onChange }) {
  return (
    <div className="text-xs mt-2 p-2 border border-amber-300 bg-amber-50 text-amber-900" data-testid="dateless-esso-warning">
      <div className="font-semibold">This Esso file has no supplier effective date.</div>
      <div>The date cannot be verified from the file. {date ? <>It will be saved as <b>{date}</b>.</> : <b>Enter the effective date above.</b>}</div>
      <label className="flex items-center gap-2 mt-2">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid="dateless-ack-checkbox" />
        Admin-confirmed effective date — not provided by supplier.
      </label>
    </div>
  );
}


function DateCheck({ c }) {
  const skipped = c.older + c.future;
  return (
    <div className={`text-xs mt-2 p-2 border ${skipped ? "border-amber-300 bg-amber-50" : "border-zinc-200 bg-zinc-50"}`} data-testid="upload-date-check">
      <div>Expected date <b>{c.expected_effective_date}</b>: <b data-testid="date-check-matching">{c.matching}</b> matching rows
        {c.without_supplier_date ? <> · {c.without_supplier_date} rows have no supplier date (saved under the expected date)</> : null}</div>
      {skipped > 0 && (
        <div className="mt-1 text-amber-900" data-testid="date-check-skipped">
          Will be skipped: {c.older} older · {c.future} later
          <ul className="mt-1 max-h-24 overflow-auto font-mono">
            {c.skipped_rows.map((r, i) => <li key={i}>{r.station_id || "—"} {r.station_name} · {r.product} · {r.row_effective_date}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
